import invariant from "invariant";
import { classes } from "polytype";
import { omit } from "remeda";
import type { HostingTariff } from "../../article/index.js";
import { Article } from "../../article/index.js";
import { Money } from "../../base/Money.js";
import assertObjectFound from "../../base/assertObjectFound.js";
import {
  AggregateMetaData,
  DataModel,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
  extractId,
} from "../../base/index.js";
import { config } from "../../config/config.js";
import { Customer } from "../../customer/index.js";
import type { AsyncResourceVariant } from "../../react/index.js";
import { provideReact } from "../../react/index.js";
import { OrderItem } from "../OrderItem/index.js";
import {
  orderPreviewFactory,
  type HostingOrderPreview,
} from "../OrderPreview/index.js";
import { TariffChangePreview } from "../TariffChangePreview/index.js";
import type {
  OrderCreateRequestData,
  OrderData,
  OrderListItemData,
  OrderListQueryModelData,
  OrderPreviewRequestData,
  OrderStatus,
  OrderTypeData,
  TariffChangePreviewRequestData,
  TariffChangeRequestData,
} from "./types.js";

export class Order extends ReferenceModel {
  public static aggregateMetaData = new AggregateMetaData("order", "order");

  public static ofId(id: string) {
    return new Order(id);
  }

  public static find = provideReact(async (id: string) => {
    const data = await config.behaviors.order.find(id);

    if (data !== undefined) {
      return new OrderDetailed(data);
    }
  });

  public static get = provideReact(async (id: string) => {
    const order = await this.find(id);
    assertObjectFound(order, "order", id);
    return order;
  });

  public findDetailed = provideReact(
    () => Order.find(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<OrderDetailed | undefined>>;

  public getDetailed = provideReact(
    () => Order.get(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<OrderDetailed>>;

  public static query(query: OrderListQueryModelData = {}) {
    return new OrderListQuery(query);
  }

  public static async create(data: OrderCreateRequestData) {
    const { id } = await config.behaviors.order.create(data);
    return Order.ofId(id);
  }

  public static preview = provideReact(
    async (data: OrderPreviewRequestData) => {
      const previewData = await config.behaviors.order.preview(data);
      return orderPreviewFactory(previewData);
    },
  );

  public static async createTariffChange(
    tariffChangeData: TariffChangeRequestData,
  ) {
    await config.behaviors.order.createTariffChange(tariffChangeData);
  }

  public static previewTariffChange = provideReact(
    async (data: TariffChangePreviewRequestData) => {
      const previewData =
        await config.behaviors.order.previewTariffChange(data);
      return new TariffChangePreview(previewData);
    },
  );

  public static getAvailableHostingArticleSpecs = provideReact(
    async (
      tariffType: "project" | "server",
      tariff?: HostingTariff,
      resourceFilter?: string,
    ) => {
      let templateName: string;

      if (
        tariff === "Webhosting" ||
        tariff === "proSpace lite" ||
        tariff === "proSpace"
      ) {
        templateName = "Webhosting";
      } else {
        templateName = "vServer";
      }

      const articles = await Article.query({
        orderable: ["full"],
        templateNames: [templateName],
      }).execute();

      let items;

      if (!tariff || (tariffType === "server" && templateName === "vServer")) {
        items = articles.items;
      } else if (resourceFilter) {
        items = articles.items.filter(
          (a) =>
            a.name === tariff &&
            (tariff === "vServer" ||
              a.machineTypeOptimization === resourceFilter),
        );
      } else {
        items = articles.items.filter((a) => a.name === tariff);
      }

      if (tariffType === "server" && templateName === "vServer") {
        if (tariff === "Dedicated Server") {
          items = items.filter((article) => {
            return (
              article.tags &&
              Array.isArray(article.tags) &&
              article.tags.some((tag) => tag.name === "dedicated")
            );
          });

          if (resourceFilter) {
            items = items.filter((article) => {
              if (article.tags && Array.isArray(article.tags)) {
                return article.tags.some((tag) => tag.name === resourceFilter);
              }
              return false;
            });
          }
        } else {
          items = items.filter((article) => {
            return !(
              article.tags &&
              Array.isArray(article.tags) &&
              article.tags.some((tag) => tag.name === "dedicated")
            );
          });
        }
      }

      return items.map((a) => {
        invariant(a.vcpu, "vcpu not found");
        invariant(a.ram, "ram not found");
        if (tariffType === "server" && templateName === "vServer") {
          invariant(a.machineType, "machineType not found");
        }

        return {
          vcpu: a.vcpu,
          ram: a.ram,
          machineType: a.machineType,
          price: a.price,
        };
      });
    },
  );

  public static getHostingArticleWithLowestPrice = provideReact(
    async (
      tariffType: "project" | "server",
      tariff?: HostingTariff,
      resourceFilter?: string,
    ) => {
      const articles = await Order.getAvailableHostingArticleSpecs(
        tariffType,
        tariff,
        resourceFilter,
      );

      return articles.reduce(function (prev, curr) {
        return prev.price.getAmount() < curr.price.getAmount() ? prev : curr;
      });
    },
  );

  public static getProjectArticleWithLowestPrice = provideReact(
    async (tariffType: "project", tariff?: HostingTariff) => {
      const articles = await Order.getAvailableHostingArticleSpecs(
        tariffType,
        tariff,
      );

      return articles.reduce(function (prev, curr) {
        return prev.price.getAmount() < curr.price.getAmount() ? prev : curr;
      });
    },
  );

  public static getHostingArticleSpecs = provideReact(
    async (
      tariffType: "project" | "server",
      tariff: HostingTariff,
      machineType?: string,
      resourceFilter?: string,
    ) => {
      const article = (
        await Order.getAvailableHostingArticleSpecs(
          tariffType,
          tariff,
          resourceFilter,
        )
      ).find((a) => a.machineType === machineType);

      invariant(article, "article not found");

      return { vcpu: article.vcpu, ram: article.ram };
    },
  );

  public static getProjectArticleSpecs = provideReact(
    async (
      tariffType: "project",
      tariff: HostingTariff,
      specs: { vcpu: number; ram: number },
    ) => {
      const article = (
        await Order.getAvailableHostingArticleSpecs(tariffType, tariff)
      ).find((a) => a.ram === specs.ram && a.vcpu === specs.vcpu);

      invariant(article, "projectArticle not found");

      return { vcpu: article.vcpu, ram: article.ram };
    },
  );

  public static getProjectPreview = provideReact(
    async (data: {
      tariffType: "project";
      tariff: HostingTariff;
      storage: number;
      specs: { vcpu: number; ram: number };
      resourceFilter?: string;
    }) => {
      const { tariffType, tariff, storage, specs } = data;

      const projectSpecs = await Order.getProjectArticleSpecs(
        tariffType,
        tariff,
        specs,
      );

      const orderPreview = await Order.preview({
        orderData: {
          diskspaceInGiB: storage,
          spec: projectSpecs,
        },
        orderType: "projectHosting",
      });

      return {
        preview: orderPreview as HostingOrderPreview,
        specs,
      };
    },
  );

  public static getHostingPreview = provideReact(
    async (data: {
      tariffType: "project" | "server";
      tariff: HostingTariff;
      storage: number;
      machineType?: string;
      resourceFilter?: string;
    }) => {
      const { tariffType, tariff, storage, machineType, resourceFilter } = data;

      const specs = await Order.getHostingArticleSpecs(
        tariffType,
        tariff,
        machineType,
        resourceFilter,
      );

      const orderPreview = await Order.preview({
        orderData: {
          diskspaceInGiB: storage,
          machineType: machineType!,
        },
        orderType: "server",
      });
      return {
        preview: orderPreview as HostingOrderPreview,
        specs,
      };
    },
  );

  public static async createHostingOrder(data: {
    tariffType: "project" | "server";
    tariff: HostingTariff;
    storage: number;
    customerId: string;
    description: string;
    machineType?: string;
    freeTrial?: boolean;
    resourceFilter?: string;
  }) {
    const { storage, customerId, description, machineType, freeTrial } = data;

    const orderData = {
      diskspaceInGiB: storage,
      customerId,
      description,
      useFreeTrial: freeTrial,
    };

    invariant(machineType, "machineType is required for server orders");

    await Order.create({
      orderData: {
        ...orderData,
        machineType,
      },
      orderType: "server",
    });
  }

  public static async createProjectOrder(data: {
    tariffType: "project";
    tariff: HostingTariff;
    storage: number;
    customerId: string;
    description: string;
    freeTrial?: boolean;
    projectSpecs: { vcpu: number; ram: number };
  }) {
    const {
      tariffType,
      tariff,
      storage,
      customerId,
      description,
      freeTrial,
      projectSpecs,
    } = data;

    const specs = await Order.getProjectArticleSpecs(
      tariffType,
      tariff,
      projectSpecs,
    );

    const orderData = {
      diskspaceInGiB: storage,
      customerId,
      description,
      useFreeTrial: freeTrial,
    };

    await Order.create({
      orderData: {
        ...orderData,
        spec: specs,
      },
      orderType: "projectHosting",
    });
  }
}

export class OrderCommon extends classes(
  DataModel<OrderListItemData | OrderData>,
  Order,
) {
  public readonly orderNumber: string;
  public readonly status: OrderStatus;
  public readonly customer: Customer;
  public readonly summary?: Money;
  public readonly summaryRecurring?: Money;
  public readonly summaryNonRecurring?: Money;
  public readonly dueDate?: Date;
  public readonly orderDate?: Date;
  public readonly items?: OrderItem[];
  public readonly type: OrderTypeData;

  public constructor(data: OrderData | OrderListItemData) {
    super([data], [data.orderId]);
    this.orderNumber = data.orderNumber;
    this.status = data.status;
    this.customer = Customer.ofId(data.customerId);
    if (data.summary) {
      this.summary = Money({ amount: data.summary.summary, currency: "EUR" });
      this.summaryRecurring = Money({
        amount: data.summary.recurring,
        currency: "EUR",
      });
      this.summaryNonRecurring = Money({
        amount: data.summary.nonRecurring,
        currency: "EUR",
      });
    }
    this.orderDate = data.orderDate ? new Date(data.orderDate) : undefined;
    this.dueDate = data.dueDate ? new Date(data.dueDate) : undefined;
    if (data.items) {
      this.items = data.items.map((i) => new OrderItem(i));
    }
    this.type = data.type;
  }

  public getOrderItemAttribute(key: string) {
    return (this.items ?? [])
      .flatMap((i) => i.attributeConfiguration)
      .find((i) => i?.key === key);
  }
}

export class OrderDetailed extends classes(OrderCommon, DataModel<OrderData>) {
  public constructor(data: OrderData) {
    super([data], [data]);
  }
}

export class OrderListItem extends classes(
  OrderCommon,
  DataModel<OrderListItemData>,
) {
  public constructor(data: OrderListItemData) {
    super([data], [data]);
  }
}

export class OrderListQuery extends ListQueryModel<OrderListQueryModelData> {
  public refine(query: OrderListQueryModelData) {
    return new OrderListQuery({
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { items, totalCount } =
      "customer" in this.query
        ? await config.behaviors.order.listByCustomer({
            ...omit(this.query, ["customer"]),
            customerId: extractId(this.query.customer)!,
          })
        : "project" in this.query
          ? await config.behaviors.order.listByProject({
              ...omit(this.query, ["project"]),
              projectId: extractId(this.query.project)!,
            })
          : await config.behaviors.order.list(this.query);

    return new OrderList(
      this.query,
      items.map((d) => new OrderListItem(d)),
      totalCount,
    );
  }, [this.queryId, "order"]);

  public executeOptional = provideReact(async () => {
    try {
      return await this.execute();
    } catch {
      return new OrderList(this.query, [], 0);
    }
  });

  public getTotalCount = provideReact(async () => {
    const { totalCount } = await this.refine({ limit: 1 }).execute();
    return totalCount;
  }, [this.queryId, "order"]);
}

export class OrderList extends classes(
  OrderListQuery,
  ListDataModel<OrderListItem>,
) {
  public constructor(
    query: OrderListQueryModelData,
    orders: OrderListItem[],
    totalCount: number,
  ) {
    super([query], [orders, totalCount]);
  }
}
